import React from "react"
import { format } from "date-fns"

import type { LockDisplayData } from "@/domains/lock-page/components/card/0.story-mocks"
import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { Separator } from "@/components/separator"
import { VISUAL_COMPONENT_HIGHLIGHTED_BACKGROUNDS } from "@/domains/colors/colorsets"
import { ColorSet } from "@/domains/colors/types"
import { ArrowRightIcon, CalendarIcon } from "@/domains/icons"
import { cn } from "@/domains/library"
import { ExtendDurationExpiredPanel } from "@/domains/lock-page/components/card/extend-duration-card/composables/extend-duration-expired-panel/extend-duration-expired-panel"
import { LockExtendDurationVisualizer } from "@/domains/lock-page/components/card/extend-duration-card/composables/lock-extend-duration-visualizer/lock-extend-duration-visualizer"
import { EpochSlider } from "@/features/lock"

const getColorFromSet = (diff: number, isScalest: boolean, colorSet: ColorSet) => {
  if (diff === 0) return colorSet.default
  return isScalest ? colorSet.scalest : colorSet.scalar
}

export interface ExtendDurationCardProps {
  nftId: number
  currentLock: LockDisplayData
  futureLock: LockDisplayData | null
  numWeeks: number
  minWeeks: number
  maxWeeks: number
  currentExpiryDate: Date
  futureExpiryDate: Date | null
  vestedTokenSymbol: string
  hasExpired: boolean
  canExtend: boolean
  icon?: React.ReactElement
  onWeeksChange: (weeks: number) => void
  onExtend: () => void
}

export const ExtendDurationCard = ({
  nftId,
  currentLock,
  futureLock,
  numWeeks,
  minWeeks,
  maxWeeks,
  currentExpiryDate,
  futureExpiryDate,
  vestedTokenSymbol,
  hasExpired,
  canExtend,
  icon,
  onWeeksChange,
  onExtend,
}: ExtendDurationCardProps) => {
  const trackRangeBg = getColorFromSet(1, currentLock.isScalest, VISUAL_COMPONENT_HIGHLIGHTED_BACKGROUNDS)

  return (
    <Card.Container
      className={cn("flex w-full flex-col gap-6", "rounded-lg border border-p400-light bg-p400 p-4 lg:p-6")}
    >
      <Card.Header>
        {icon && (
          <Card.HeaderIconContainer>
            {React.cloneElement(icon, { className: "text-t200", size: 24 })}
          </Card.HeaderIconContainer>
        )}

        <div className="flex w-full flex-col">
          <Card.HeaderTitle>Extend Lock Duration · #{nftId}</Card.HeaderTitle>
          <Card.HeaderDescription>Extend the duration of your {vestedTokenSymbol} NFT lock</Card.HeaderDescription>
        </div>
      </Card.Header>
      <Separator
        className="relative h-0 w-full overflow-visible rounded-md border border-solid border-p400-light
      after:pointer-events-none after:absolute after:inset-[-1px] after:z-[1] after:rounded-md 
      after:shadow-[inset_0.5px_0.5px_0.5px_0px_hsla(220,43%,5%,1)] after:content-['']"
        decorative
      />
      {hasExpired ? (
        <ExtendDurationExpiredPanel />
      ) : (
        <>
          <div className="space-y-4">
            <div className="flex flex-col gap-1">
              <LockExtendDurationVisualizer
                currentLock={currentLock}
                futureLock={futureLock ?? currentLock}
                vestedTokenSymbol={vestedTokenSymbol}
              />
            </div>
          </div>
          <div className="flex flex-col gap-4 rounded-lg border border-p400-light bg-p400 px-4 py-2">
            <div className="flex flex-row items-center justify-between">
              <div className="flex flex-row items-center gap-1.5 text-t200">
                <CalendarIcon className="text-t200" size={18} /> {format(currentExpiryDate, "MMM do, yyyy")}{" "}
                <ArrowRightIcon className="text-t200" size={16} />{" "}
                {format(futureExpiryDate ?? currentExpiryDate, "MMM do, yyyy")}
              </div>
              <div className="text-xs text-t200">Time Locked (weeks)</div>
            </div>
            <div className="min-h-[54px]">
              <EpochSlider
                initialValue={numWeeks}
                minWeeks={minWeeks}
                onChange={onWeeksChange}
                trackRangeBg={trackRangeBg}
              />
            </div>
          </div>
        </>
      )}
      <Card.Buttons>
        <Button disabled={!canExtend} variant={"primary"} onClick={onExtend}>
          Extend Lock Duration
        </Button>
      </Card.Buttons>
    </Card.Container>
  )
}
